import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LiveKitRoom, useLocalParticipant, useTracks, RoomAudioRenderer, useParticipants, useRoomContext } from '@livekit/components-react';
import '@livekit/components-styles';
import { Track, ParticipantEvent, RoomEvent } from 'livekit-client';
import { Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, Users, Loader2, User, Mail, Lock, BellRing, Clock } from 'lucide-react';

const API_BASE = 'https://lms.servall.in/guest-api';
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const circleBtn = (bg) => ({ width: 56, height: 56, borderRadius: '50%', border: 'none', background: bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });

function VideoTile({ track, label, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track?.publication?.track) return;
    track.publication.track.attach(el);
    return () => { try { track.publication.track.detach(el); } catch {} };
  }, [track]);
  return (
    <div style={{ position: 'relative', background: '#222', borderRadius: 8, overflow: 'hidden', ...style }}>
      <video ref={ref} autoPlay playsInline muted={track?.participant?.isLocal} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {label && <div style={{ position: 'absolute', bottom: 6, left: 8, color: '#fff', fontSize: 12, textShadow: '0 1px 4px rgba(0,0,0,0.9)', fontWeight: 500 }}>{label}</div>}
    </div>
  );
}

function ScreenTile({ track }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track?.publication?.track) return;
    track.publication.track.attach(el);
    return () => { try { track.publication.track.detach(el); } catch {} };
  }, [track]);
  return <video ref={ref} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }} />;
}

// Waiting Room screen — shown after guest joins LiveKit but before host approves
function WaitingRoom({ guestName, guestIdentity, meetingId, meetingTitle, hostName, onApproved, onDenied }) {
  const room = useRoomContext();
  const [knocked, setKnocked] = useState(false);

  useEffect(() => {
    // Send knock as soon as we're in the room
    const sendKnock = async () => {
      try {
        await fetch(`${API_BASE}/meeting/${meetingId}/knock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guestName, guestIdentity }),
        });
        setKnocked(true);
      } catch {}
    };
    sendKnock();

    // Listen for host's response
    const onData = (payload) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg.type === 'knock_response') {
          if (msg.approved) onApproved();
          else onDenied();
        }
      } catch {}
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => room.off(RoomEvent.DataReceived, onData);
  }, [room, guestName, guestIdentity, meetingId, onApproved, onDenied]);

  // DB polling fallback - check if host approved via DB (in case LiveKit signal missed)
  useEffect(() => {
    if (!meetingId || !guestIdentity) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/meeting/${meetingId}/knock-status?guestIdentity=${encodeURIComponent(guestIdentity)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'approved') onApproved();
          else if (data.status === 'denied') onDenied();
        }
      } catch {}
    };
    const timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  }, [meetingId, guestIdentity, onApproved, onDenied]);

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: 20 }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(124,58,237,0.15)', border: '2px solid #7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', animation: 'pulse 2s ease-in-out infinite' }}>
          <Clock size={32} color="#a78bfa" />
        </div>
        <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Waiting to be admitted</h1>
        <p style={{ color: '#888', fontSize: 14, marginBottom: 6 }}>
          <strong style={{ color: '#ccc' }}>{hostName || 'The host'}</strong> will let you in soon
        </p>
        {meetingTitle && <p style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>{meetingTitle}</p>}
        <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '14px 20px', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Loader2 size={16} color="#7c3aed" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
          <span style={{ color: '#aaa', fontSize: 13 }}>
            {knocked ? 'Request sent — please wait...' : 'Connecting...'}
          </span>
        </div>
        <p style={{ color: '#444', fontSize: 12, marginTop: 20 }}>You can now close this tab if you no longer wish to join</p>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.4) } 50% { box-shadow: 0 0 0 12px rgba(124,58,237,0) } }
      `}</style>
    </div>
  );
}

function GuestRoom({ onLeave, meetingId, guestIdentity }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [screenAllowed, setScreenAllowed] = useState(false);
  const [toast, setToast] = useState(null);
  const [unmuteRequest, setUnmuteRequest] = useState(null);

  const cameraTracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const screenTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: false });
  const hasScreen = screenTracks.length > 0;

  const showToast = (msg, color) => { setToast({ msg, color }); setTimeout(() => setToast(null), 3500); };

  // Enable mic/cam once approved (was disabled in waiting room)
  useEffect(() => {
    localParticipant.setMicrophoneEnabled(true).catch(() => {});
    localParticipant.setCameraEnabled(true).catch(() => {});
  }, [localParticipant]);

  useEffect(() => {
    const onAttrsChanged = () => {
      const allowed = localParticipant.attributes?.screenShareAllowed === 'true';
      setScreenAllowed(prev => {
        if (allowed && !prev) showToast('Host allowed you to share your screen', '#1a7f4b');
        if (!allowed && prev) {
          showToast('Host revoked your screen share permission', '#dc2626');
          localParticipant.setScreenShareEnabled(false).catch(() => {});
          setScreenSharing(false);
        }
        return allowed;
      });
    };
    localParticipant.on(ParticipantEvent.AttributesChanged, onAttrsChanged);
    return () => localParticipant.off(ParticipantEvent.AttributesChanged, onAttrsChanged);
  }, [localParticipant]);

  useEffect(() => {
    const onData = (payload, participant, kind, topic) => {
      if (topic !== 'unmute_request') return;
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg.type === 'unmute_request') setUnmuteRequest({ kind: msg.kind });
      } catch {}
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => room.off(RoomEvent.DataReceived, onData);
  }, [room]);

  const acceptUnmuteRequest = async () => {
    if (!unmuteRequest) return;
    try {
      if (unmuteRequest.kind === 'mic') { await localParticipant.setMicrophoneEnabled(true); setMicOn(true); }
      else { await localParticipant.setCameraEnabled(true); setCamOn(true); }
      showToast(`You turned on your ${unmuteRequest.kind === 'mic' ? 'mic' : 'camera'}`, '#1a7f4b');
    } catch { showToast('Could not enable - check permissions', '#dc2626'); }
    setUnmuteRequest(null);
  };

  const toggleMic = useCallback(async () => { await localParticipant.setMicrophoneEnabled(!micOn); setMicOn(v => !v); }, [localParticipant, micOn]);
  const toggleCam = useCallback(async () => { await localParticipant.setCameraEnabled(!camOn); setCamOn(v => !v); }, [localParticipant, camOn]);

  const toggleScreen = useCallback(async () => {
    if (!screenAllowed) { showToast('Ask the host to allow screen sharing', '#dc2626'); return; }
    if (isMobile()) { showToast('Screen share not supported on mobile browsers', '#dc2626'); return; }
    if (!screenSharing) {
      try { await localParticipant.setScreenShareEnabled(true); setScreenSharing(true); showToast('Screen sharing started', '#1a7f4b'); }
      catch { showToast('Screen share cancelled or denied', '#dc2626'); }
    } else {
      await localParticipant.setScreenShareEnabled(false); setScreenSharing(false);
    }
  }, [localParticipant, screenSharing, screenAllowed]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#111' }}>
      <div style={{ padding: '10px 16px', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Servall Meet</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: '5px 12px' }}>
          <Users size={13} color="#fff" /><span style={{ color: '#fff', fontSize: 13 }}>{participants.length}</span>
        </div>
      </div>

      {toast && <div style={{ position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)', background: toast.color, color: '#fff', padding: '8px 20px', borderRadius: 20, fontSize: 13, zIndex: 200, whiteSpace: 'nowrap', maxWidth: '85%' }}>{toast.msg}</div>}

      {unmuteRequest && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#1a1a1a', borderRadius: 14, padding: 24, maxWidth: 320, textAlign: 'center' }}>
            <BellRing size={32} color="#a78bfa" style={{ marginBottom: 12 }} />
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
              Host wants you to {unmuteRequest.kind === 'mic' ? 'unmute your mic' : 'turn on your camera'}
            </div>
            <div style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>You can turn it off again anytime</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setUnmuteRequest(null)} style={{ flex: 1, background: '#333', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', cursor: 'pointer', fontSize: 13 }}>Dismiss</button>
              <button onClick={acceptUnmuteRequest} style={{ flex: 1, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Allow</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'hidden', padding: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {hasScreen ? (
          <>
            <div style={{ flex: 1, borderRadius: 8, overflow: 'hidden', background: '#000', minHeight: 0 }}>
              <ScreenTile track={screenTracks[0]} />
            </div>
            <div style={{ height: 90, display: 'flex', gap: 6, flexShrink: 0 }}>
              {cameraTracks.map((t, i) => <VideoTile key={i} track={t} label={t.participant?.name} style={{ flex: 1, height: 90 }} />)}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'grid', gap: 6, minHeight: 0, gridTemplateColumns: cameraTracks.length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
            {cameraTracks.map((t, i) => <VideoTile key={i} track={t} label={t.participant?.name} style={{ minHeight: 0 }} />)}
          </div>
        )}
      </div>
      <RoomAudioRenderer />
      <div style={{ padding: '14px 20px 18px', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexShrink: 0 }}>
        <button style={circleBtn(micOn ? '#3c3c3c' : '#dc2626')} onClick={toggleMic}>{micOn ? <Mic size={22} color="#fff" /> : <MicOff size={22} color="#fff" />}</button>
        <button style={circleBtn(camOn ? '#3c3c3c' : '#dc2626')} onClick={toggleCam}>{camOn ? <Video size={22} color="#fff" /> : <VideoOff size={22} color="#fff" />}</button>
        {!isMobile() && (
          <button style={circleBtn(screenSharing ? '#1a7f4b' : screenAllowed ? '#3c3c3c' : '#2a2a2a')} onClick={toggleScreen} title={screenAllowed ? 'Share screen' : 'Ask host for permission'}>
            {screenAllowed ? <MonitorUp size={22} color="#fff" /> : <Lock size={18} color="#777" />}
          </button>
        )}
        <button style={circleBtn('#dc2626')} onClick={onLeave}><PhoneOff size={22} color="#fff" /></button>
      </div>
    </div>
  );
}

function DeniedPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ textAlign: 'center', color: '#fff', padding: 32 }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🚫</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Entry Declined</h1>
        <p style={{ color: '#888', fontSize: 14, marginBottom: 6 }}>The host has declined your request to join</p>
        <p style={{ color: '#555', fontSize: 13 }}>You can now close this tab</p>
      </div>
    </div>
  );
}

function ThankYouPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>👋</div>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>You have left the meeting</h1>
        <p style={{ color: '#888', fontSize: 15 }}>Thank you for joining Servall Meet</p>
        <p style={{ color: '#555', fontSize: 13, marginTop: 6 }}>You can now close this tab</p>
      </div>
    </div>
  );
}

// Inner component that has access to LiveKitRoom context
function GuestRoomRouter({ meetingId, guestName, guestIdentity, meetingTitle, hostName, onLeave }) {
  const [admitted, setAdmitted] = useState(false);
  const [denied, setDenied] = useState(false);

  if (denied) return <DeniedPage />;

  if (!admitted) return (
    <WaitingRoom
      guestName={guestName}
      guestIdentity={guestIdentity}
      meetingId={meetingId}
      meetingTitle={meetingTitle}
      hostName={hostName}
      onApproved={() => setAdmitted(true)}
      onDenied={() => setDenied(true)}
    />
  );

  return <GuestRoom onLeave={onLeave} meetingId={meetingId} guestIdentity={guestIdentity} />;
}

const GuestMeetPage = () => {
  const { meetingId } = useParams();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('form');
  const [name, setName] = useState(searchParams.get('name') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [token, setToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [meetingInfo, setMeetingInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const joinMeeting = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/meeting/${meetingId}/token?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`);
      if (!res.ok) throw new Error('Meeting not found or has ended');
      const data = await res.json();
      setToken(data.token);
      setLivekitUrl(data.livekitUrl);
      setMeetingInfo({
        participantIdentity: data.participantIdentity,
        meetingTitle: data.meetingTitle,
        hostName: data.hostName,
      });
      setStep('meeting');
    } catch (err) { setError(err.message || 'Failed to join meeting'); }
    finally { setLoading(false); }
  };

  if (step === 'thankyou') return <ThankYouPage />;

  if (step === 'form') return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: 16 }}>
      <div style={{ background: '#1a1a1a', borderRadius: 16, padding: '40px 32px', width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ background: '#dc2626', borderRadius: 12, width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Video size={28} color="#fff" />
          </div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: 0 }}>Join Meeting</h1>
          <p style={{ color: '#888', fontSize: 14, marginTop: 6 }}>Powered by Servall LMS</p>
        </div>
        {error && <div style={{ background: '#3f1a1a', border: '1px solid #dc2626', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#f87171', fontSize: 14 }}>{error}</div>}
        <form onSubmit={joinMeeting}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 6 }}>Your Name</label>
            <div style={{ position: 'relative' }}>
              <User size={16} color="#666" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your full name" required style={{ width: '100%', background: '#2a2a2a', border: '1px solid #333', borderRadius: 8, padding: '11px 12px 11px 36px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 6 }}>Email Address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={16} color="#666" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required style={{ width: '100%', background: '#2a2a2a', border: '1px solid #333', borderRadius: 8, padding: '11px 12px 11px 36px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <button type="submit" disabled={loading} style={{ width: '100%', background: loading ? '#555' : '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '13px', fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {loading ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Joining...</> : 'Join Meeting'}
          </button>
        </form>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#111', zIndex: 9999 }}>
      {token && livekitUrl && (
        <LiveKitRoom
          token={token}
          serverUrl={livekitUrl}
          connect={true}
          video={false}
          audio={false}
          onDisconnected={() => setStep('thankyou')}
          style={{ height: '100%' }}
        >
          <GuestRoomRouter
            meetingId={meetingId}
            guestName={name}
            guestIdentity={meetingInfo.participantIdentity}
            meetingTitle={meetingInfo.meetingTitle}
            hostName={meetingInfo.hostName}
            onLeave={() => setStep('thankyou')}
          />
        </LiveKitRoom>
      )}
    </div>
  );
};

export default GuestMeetPage;
